import { useDispatch, useSelector } from 'react-redux';
import { ISaveOffersFromListingV1Response } from 'shared/common/repositories/mortgage-broker-calculator/entities/save_offers_from_listing/SaveOffersFromListingV1Response';
import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { mortgageMiniCalculatorApi } from '../api';
import { closeModal, openAuthorizationModal, setIsErrorGetOffersListing, setSaveOfferResult } from '../redux';
import { selectProgramsVersion, selectRealEstateType, selectSaveOffersParams } from '../selectors/calculator';
import { ETypeAction } from '../types';
import { redirect } from '../utils';
import { useGetOffers } from './useGetOffers';

interface IUseSaveOffersOptions {
  redirectUrl?: string;
  defaultRedirectUrl: string;
}

interface IUseSaveOffersResult {
  isLoading: boolean;
  saveOffers: (typeAction: ETypeAction) => Promise<void>;
  shouldUseAuthActionV3: boolean;
}

export const useSaveOffers = (options: IUseSaveOffersOptions): IUseSaveOffersResult => {
  const { redirectUrl, defaultRedirectUrl } = options;
  const { logger, config } = useApplicationContext();
  const dispatch = useDispatch();

  const isEnableGetOffersV3 = config.getStrict(ERuntimeSettings.enableGetOffersV3);
  const realEstateType = useSelector(selectRealEstateType);
  const programsVersion = useSelector(selectProgramsVersion);
  const saveOffersParams = useSelector(selectSaveOffersParams);

  const { refetch: refetchOffers } = useGetOffers();
  const [saveOffersFromListing, { isLoading }] =
    mortgageMiniCalculatorApi.endpoints.saveOffersFromListing.useMutation();

  const saveOffersResultToRedux = (result: ISaveOffersFromListingV1Response) => {
    dispatch(
      setSaveOfferResult({
        applicationId: result.applicationId,
        redirectUrl: result.redirectUrl,
      }),
    );
  };

  const handleInvalidProgramsVersionError = async (typeAction: ETypeAction) => {
    try {
      // Получаем новый programsVersion
      await refetchOffers();
    } catch (retryError) {
      logger.error(retryError, {
        domain: 'useSaveOffers',
        description: `Ошибка при получении офферов после saveOffersFromListing (${typeAction})`,
      });
    }
  };

  const saveOffers = async (typeAction: ETypeAction) => {
    try {
      const result = await saveOffersFromListing(saveOffersParams).unwrap();

      if (typeAction === ETypeAction.AUTH_AND_REDIRECT) {
        dispatch(openAuthorizationModal());
        saveOffersResultToRedux(result);
      } else {
        redirect(result?.redirectUrl || redirectUrl || defaultRedirectUrl);
      }
    } catch (error) {
      logger.error(error, {
        domain: 'useSaveOffers',
        description: `Ошибка при сохранение офферов (${typeAction})`,
      });

      if (error.message === 'Invalid programs version') {
        await handleInvalidProgramsVersionError(typeAction);
      }

      dispatch(closeModal());
      dispatch(setIsErrorGetOffersListing(true));
    }
  };

  return {
    isLoading,
    saveOffers,
    shouldUseAuthActionV3: Boolean(isEnableGetOffersV3 && programsVersion && realEstateType),
  };
};
